// Radha — workflow automation rules (045). Rules run hourly via pg_cron
// (run_all_automations, not client-callable) and on demand via run_my_automations.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

export type RuleType = "crm_idle_deal_note" | "dispute_sla_log" | "collections_auto_proposals";

export interface AutomationRule {
  id: string; rule_type: RuleType; threshold_days: number; is_active: boolean; last_run_at: string | null;
}
export interface AutomationLogEntry { id: string; rule_type: string; summary: string; created_at: string }

export const RULE_LABEL: Record<RuleType, string> = {
  crm_idle_deal_note: "Flag idle CRM deals",
  dispute_sla_log: "Log SLA-breaching disputes",
  collections_auto_proposals: "Auto-generate collection proposals",
};
export const RULE_HINT: Record<RuleType, string> = {
  crm_idle_deal_note: "Adds a note to any open deal untouched past the threshold — visible in its activity timeline.",
  dispute_sla_log: "Logs a daily rollup of open disputes past their resolution SLA (7d high-value, else 15d).",
  collections_auto_proposals: "Runs the same Tier-0 rules as the Collections \"Generate proposals\" button, automatically.",
};
export const ALL_RULE_TYPES: RuleType[] = ["crm_idle_deal_note", "dispute_sla_log", "collections_auto_proposals"];

const RULES_KEY = ["automation-rules"];
const LOG_KEY = ["automation-log"];

export function useAutomationRules() {
  return useQuery({
    queryKey: RULES_KEY,
    queryFn: async (): Promise<AutomationRule[]> => {
      const { data, error } = await supabase.from("automation_rules")
        .select("id, rule_type, threshold_days, is_active, last_run_at");
      if (error) throw error;
      return (data ?? []) as AutomationRule[];
    },
  });
}

export function useAutomationLog() {
  return useQuery({
    queryKey: LOG_KEY,
    queryFn: async (): Promise<AutomationLogEntry[]> => {
      const { data, error } = await supabase.from("automation_log")
        .select("id, rule_type, summary, created_at")
        .order("created_at", { ascending: false }).limit(30);
      if (error) throw error;
      return (data ?? []) as AutomationLogEntry[];
    },
  });
}

export function useToggleRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ruleType, threshold, existing }: { ruleType: RuleType; threshold: number; existing?: AutomationRule }) => {
      if (existing) {
        const { error } = await supabase.from("automation_rules").update({ is_active: !existing.is_active }).eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("automation_rules").insert({ rule_type: ruleType, threshold_days: threshold });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_KEY }),
  });
}

export function useRunAutomationsNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc("run_my_automations");
      if (error) throw error;
      return (data as number) ?? 0;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RULES_KEY });
      qc.invalidateQueries({ queryKey: LOG_KEY });
      qc.invalidateQueries({ queryKey: ["crm-activities"] });
      qc.invalidateQueries({ queryKey: ["collections-direct", "proposals"] });
    },
  });
}
